/**
 * crossTab — small primitives for coordinating work across same-origin tabs.
 *
 * `runAsLeader` elects exactly one tab to run app-singleton work using the Web
 * Locks API: the lock is held (and `onAcquire` stays in effect) until the tab
 * closes, at which point another waiting tab acquires it and takes over.
 *
 * Graceful fallback: if Web Locks is unavailable, `onAcquire` runs immediately,
 * i.e. every tab acts as its own leader — the pre-cross-tab behaviour.
 */

type LocksLike = {
  request: (
    name: string,
    options: { signal?: AbortSignal },
    callback: () => Promise<unknown>,
  ) => Promise<unknown>;
};

const getLocks = (): LocksLike | undefined =>
  (typeof navigator !== "undefined" ? (navigator as unknown as { locks?: LocksLike }).locks : undefined);

/** True only when both primitives needed for cross-tab single-execution exist. */
export const crossTabSupported = (): boolean =>
  typeof BroadcastChannel !== "undefined" && !!getLocks();

/**
 * Run `onAcquire` once this tab becomes the leader for `lockName`. Returns a
 * disposer that relinquishes leadership (rarely needed — the store lives for the
 * tab's lifetime).
 */
export function runAsLeader(lockName: string, onAcquire: () => void): () => void {
  const locks = getLocks();
  if (!locks) {
    onAcquire();
    return () => {};
  }

  const controller = new AbortController();
  // The lock is held for as long as this promise is unresolved — i.e. until the
  // tab closes or we abort. While held, this tab is the leader.
  locks
    .request(lockName, { signal: controller.signal }, () => new Promise<void>(() => onAcquire()))
    .catch(() => {
      /* aborted or rejected — we simply never became leader */
    });

  return () => controller.abort();
}
